import { useQuery } from '@tanstack/react-query';

import moment from 'moment';

import _ from 'lodash';

import { Promise } from 'bluebird';

import { roomTypesService } from '../../services/homii-services';

import { mediaUtil } from '../utils';

export const ArrangementTypes = Object.freeze({ PRIVATE: 'Private', SHARED: 'Shared' });

async function prepareRoomType(room, building, type) {
    const { name, address, amenities, details } = building;
    const roomWithBuildingProps = room;
    const { documents, id } = roomWithBuildingProps;
    const { featuredAmenities } = roomWithBuildingProps;
    const allDocuments = _.concat(documents, featuredAmenities);
    const {
        roomImages,
        floorplanImages,
        featuredAmenityImages,
    } = mediaUtil.generateEntityImageSources(allDocuments, id, 'roomType');

    roomWithBuildingProps.buildingName = name;
    roomWithBuildingProps.buildingAddress = address;
    roomWithBuildingProps.buildingAmenities = amenities;
    roomWithBuildingProps.buildingDescription = details;
    roomWithBuildingProps.arrangementType = type;
    roomWithBuildingProps.images = _.get(roomImages, 'imageSources');
    roomWithBuildingProps.thumbnails = _.get(roomImages, 'thumbnailSources');
    roomWithBuildingProps.floorplanImages = _.get(floorplanImages, 'floorplanSources');
    roomWithBuildingProps.floorplanThumbnails = _.get(floorplanImages, 'floorplanThumbnailSources');
    roomWithBuildingProps.featuredAmenityImages = _.get(
        featuredAmenityImages,
        'featuredAmenitySources',
    );
    roomWithBuildingProps.featuredAmenityThumbnails = _.get(
        featuredAmenityImages,
        'featuredAmenityThumbnailSources',
    );

    delete roomWithBuildingProps.documents;
    return roomWithBuildingProps;
};

function prepareRoomTypesForBuilding(room, building, type) {
    const { name, address, amenities, details } = building;
    const roomWithBuildingProps = room;
    const { documents } = roomWithBuildingProps.ids[0];
    const id = roomWithBuildingProps.ids[0].roomTypeId;
    const { featuredAmenities } = roomWithBuildingProps;
    const allDocuments = _.concat(documents, featuredAmenities);
    const {
        roomImages,
        floorplanImages,
        featuredAmenityImages,
    } = mediaUtil.generateEntityImageSources(allDocuments, id, 'roomType');

    roomWithBuildingProps.buildingName = name;
    roomWithBuildingProps.buildingAddress = address;
    roomWithBuildingProps.buildingAmenities = amenities;
    roomWithBuildingProps.buildingDescription = details;
    roomWithBuildingProps.arrangementType = type;
    roomWithBuildingProps.images = _.get(roomImages, 'imageSources');
    roomWithBuildingProps.thumbnails = _.get(roomImages, 'thumbnailSources');
    roomWithBuildingProps.floorplanImages = _.get(floorplanImages, 'floorplanSources');
    roomWithBuildingProps.floorplanThumbnails = _.get(floorplanImages, 'floorplanThumbnailSources');
    roomWithBuildingProps.featuredAmenityImages = _.get(
        featuredAmenityImages,
        'featuredAmenitySources',
    );
    roomWithBuildingProps.featuredAmenityThumbnails = _.get(
        featuredAmenityImages,
        'featuredAmenityThumbnailSources',
    );

    delete roomWithBuildingProps.documents;
    return roomWithBuildingProps;
};

function setAvailabilityForRoomType(availability, _roomType) {
    const roomType = _roomType;
    roomType.soonestAvailable = _.get(availability, 'soonestAvailable');
    roomType.numberAvailable = _.get(availability, 'numberAvailable');
    roomType.acceptingProvisionalBookings = _.get(availability, 'acceptingProvisionalBookings');
    roomType.numberAvailableForMales = _.get(
        availability,
        'sharedRoomSplit.numberAvailableForMales',
    );
    roomType.numberAvailableForFemales = _.get(
        availability,
        'sharedRoomSplit.numberAvailableForFemales',
    );

    return roomType;
}

const prepareRoomTypes = async (buildings) => {

    const roomTypes = {};

    for (const building of buildings) {

        const { privateRoomTypes, sharedRoomTypes } = building;

        for (const roomType of privateRoomTypes) {
            roomTypes[roomType.id] = await prepareRoomType(roomType, building, ArrangementTypes.PRIVATE);
        }

        for (const roomType of sharedRoomTypes) {
            roomTypes[roomType.id] = await prepareRoomType(roomType, building, ArrangementTypes.SHARED);
        }
    }

    return roomTypes;
};

async function fetchAvailabilityForCity(selectedCityId) {

    try {
    
        const startDate = moment().startOf('month').format('YYYY-MM-DD');
        
        const endDate = moment().add(1, 'months').startOf('month').format('YYYY-MM-DD');
        
        const buildingsWithRoomTypes = await roomTypesService.searchAvailable(selectedCityId, startDate, endDate);

        const roomTypeObjects = await prepareRoomTypes(buildingsWithRoomTypes.buildings);

        const sharedRoomTypes = [];

        let roomTypes = Object.values(roomTypeObjects);

        roomTypes
        .filter((roomType) => !!roomType) 
        .forEach((roomType) => {
            if (roomType.arrangementType === 'Shared' && !roomType.gender) {
                const maleRoomType = {
                    ...roomType,
                    gender: 'male',
                    numberAvailable: _.get(roomType, 'numberAvailableForMales', 0),
                };
                const femaleRoomType = {
                    ...roomType,
                    gender: 'female',
                    numberAvailable: _.get(roomType, 'numberAvailableForFemales', 0),
                    id: `${roomType.id}___female`,
                };
                sharedRoomTypes.push(maleRoomType, femaleRoomType);
            }
        });			

        roomTypes = _.concat(roomTypes, sharedRoomTypes);

        const updatedRoomTypeObjects = {};

        roomTypes.forEach((roomType) => {
            updatedRoomTypeObjects[roomType.id] = roomType;
        });			

        return updatedRoomTypeObjects;        
        
    } catch(error) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to fetch city availability: ${error.message}`);  
        throw Error(
            `${_.get(
                error,
                'response.data.message',
                'Failed to fetch city availability.',
            )} Request ID: ${_.get(error, 'response.headers.x-request-id', 'unknown')}`,
        );              
    }
};

async function checkRoomTypeAvailabilityPerBuilding(selectedBuilding) {
    
    const startDate = moment().startOf('month').format('YYYY-MM-DD');

    const endDate = moment().add(1, 'months').startOf('month').format('YYYY-MM-DD');

    const availability = await roomTypesService.getRoomTypeSearchAvailableByBuilding(
        _.get(selectedBuilding, 'id'),
        startDate,
        endDate
    );

    const allRoomTypes = [];

    await Promise.map(availability.privateRoomTypes, async (privateRoomType) => {
        const roomType = await prepareRoomTypesForBuilding(
            privateRoomType,
            selectedBuilding,
            ArrangementTypes.PRIVATE
        );
        allRoomTypes.push(roomType);
    });
    
    await Promise.map(availability.sharedRoomTypes, async (sharedRoomType) => {
        const roomType = await prepareRoomTypesForBuilding(
            sharedRoomType,
            selectedBuilding,
            ArrangementTypes.SHARED
        );
        allRoomTypes.push(roomType);
    });

    return allRoomTypes;    

};

async function fetchAllCityRoomTypes(fetchedCities) {
    const cities = _.map(fetchedCities, (city) => city);
    const allRoomTypes = [];
    await Promise.map(cities, async (_city) => {
        const city = _city;
        const roomTypes = await roomTypesService.getRoomTypeByCityId(city.id);
        _.forEach(roomTypes.privateRoomTypes, (privateRoomType) => {
            allRoomTypes.push(privateRoomType);
        });
        _.forEach(roomTypes.sharedRoomTypes, (sharedRoomType) => {
            allRoomTypes.push(sharedRoomType);
        });
    });
    return allRoomTypes;
};

async function fetchRoomTypeSummaryAvailabilityPerBuilding(selectedBuilding) {    
    const summaries = [];
    const summaryResults = await roomTypesService.getRoomTypeSummaryAvailableByBuilding(
        _.get(selectedBuilding, 'id'),
    );
    _.forEach(summaryResults.summary, async (summary) => {
        summaries.push(summary);
    });
    return summaries;    
}

async function fetchRoomTypeById(buildings, roomTypeId) {
    const roomType = await roomTypesService
        .getRoomTypeById(roomTypeId)
        .then(async (roomType) => {
            const building = buildings[roomType.buildingId];
            const arrangementType = roomType.sharedLivingSpace
                ? ArrangementTypes.SHARED
                : ArrangementTypes.PRIVATE;
            let startDate = moment().startOf('month').format('YYYY-MM-DD');
            let endDate = moment().add(1, 'months').startOf('month').format('YYYY-MM-DD');            
            const availability = await roomTypesService.getRoomTypeAvailability(
                roomTypeId,
                startDate,
                endDate,
            );
            const roomTypeWithAvailability = setAvailabilityForRoomType(
                availability,
                roomType,
            );                 
            const processedRoomType = prepareRoomType(roomTypeWithAvailability, building, arrangementType);           
            return processedRoomType;            
        });
    return roomType;
};

async function fetchRoomTypeAvailability(roomTypeId) {    
    try {
        let startDate = moment().startOf('month').format('YYYY-MM-DD');
        let endDate = moment().add(1, 'months').startOf('month').format('YYYY-MM-DD');
        const availability = await roomTypesService.getRoomTypeAvailability(
            roomTypeId,
            startDate,
            endDate,
        );
        const roomTypeWithAvailability = setAvailabilityForRoomType(
            availability,
            selectedRoomType,
        );
        return roomTypeWithAvailability;
    } catch(error) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to fetch room type availability: ${error.message}`);        
    }    
}

export const useSelectedRoomTypeData = (buildings, roomTypeId) => {
    return useQuery({
        queryKey: ['roomType', roomTypeId],
        queryFn: async () =>  {
            const roomType = await roomTypesService
                .getRoomTypeById(roomTypeId)
                .then(async (roomType) => {
                    const building = buildings[roomType.buildingId];
                    const arrangementType = roomType.sharedLivingSpace
                        ? ArrangementTypes.SHARED
                        : ArrangementTypes.PRIVATE;
                    const processedRoomType = prepareRoomType(roomType, building, arrangementType);
                    return processedRoomType;            
                });
            return roomType;
        },
        staleTime: 1000 * 60 * 60, 
        enabled: !!roomTypeId,
    });
}

export default {
    fetchAvailabilityForCity,
    checkRoomTypeAvailabilityPerBuilding,
    fetchAllCityRoomTypes,
    fetchRoomTypeSummaryAvailabilityPerBuilding,
    fetchRoomTypeById,
    fetchRoomTypeAvailability
}
